import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentChartBarIcon, ChevronLeftIcon, ChevronRightIcon, Cog8ToothIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { MONTHLY_INBOUND_SHIPMENTS_VALUES } from '../../consts/reports';
import { SelectDropdown } from '../../components/SelectDropdown';
import { ReportEmailSettingsSidebar } from '../../components/reports/ReportEmailSettingsSidebar';
import { toast } from 'react-toastify';
import {
  getReportEmailSettings,
  updateReportEmailSettings,
  sendReportPreviewEmail,
} from '../../slices/reportEmailSettings';
import { getReportSendHistory } from '../../slices/reportSendHistory';
import { updateCronInformation } from '../../slices/cronSlice';
import { buildCronInterval } from '../../helpers/cronExpression';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import useDateRangeParams from '../../hooks/useDateRangeParams';
import useUrlParam from '../../hooks/useUrlParam';
import {
  isValidMonthParam,
  isValidQuarterParam,
  parseMonthParam,
  parseQuarterParam,
  serializeMonth,
  serializeQuarter,
} from '../../hooks/dateParamHelpers';

const DATE_TYPES = {
  RANGE: 'range',
  MONTH: 'month',
  QUARTER: 'quarter'
};

const quarters = [
  { label: 'Q1', startMonth: 0, endMonth: 2 },
  { label: 'Q2', startMonth: 3, endMonth: 5 },
  { label: 'Q3', startMonth: 6, endMonth: 8 },
  { label: 'Q4', startMonth: 9, endMonth: 11 }
];

function monthLabel(year, monthIndex) {
  const d = new Date(year, monthIndex, 1);
  return d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
}

function getMonthRange(year, monthIndex) {
  const start = new Date(year, monthIndex, 1);
  const end = new Date(year, monthIndex + 1, 0);
  const toYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: toYMD(start), endDate: toYMD(end) };
}

function getQuarterRange(year, qIndex) {
  const q = quarters[qIndex];
  return getMonthRange(year, q.endMonth); // temp to produce endDate, we override start below
}

function getQuarterStartEnd(year, qIndex) {
  const { startMonth, endMonth } = quarters[qIndex];
  const start = new Date(year, startMonth, 1);
  const end = new Date(year, endMonth + 1, 0);
  const toYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: toYMD(start), endDate: toYMD(end) };
}

const MonthlyInboundShipmentsValues = ({
  marginFromSidebar
}) => {
  const navigate = useNavigate();

  // Table/data state
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalByPage, setTotalByPage] = useState(0);
  const [isOpenSettings, setIsOpenSettings] = useState(false);
  const dispatch = useDispatch();
  const { settings: allSettings, loading: isLoadingEmailSettings } = useSelector((s) => s.reportEmailSettings);
  const emailSettings = allSettings['received-inventory'] || {};
  const { sendHistory: allSendHistory, loading: isLoadingSendHistory } = useSelector((s) => s.reportSendHistory);
  const sendHistory = allSendHistory['received-inventory'] || [];
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);
  // Sorting
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 50;

  // Filters
  const [poSearch, setPoSearch] = useState('');
  const [debouncedPoSearch, setDebouncedPoSearch] = useState('');
  const now = new Date();
  const defaultStartDate = (() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  })();
  const defaultEndDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const defaultMonth = { year: now.getFullYear(), monthIndex: now.getMonth() };
  const defaultQuarter = { quarterYear: now.getFullYear(), quarterIndex: Math.floor(now.getMonth() / 3) };
  const [basis, setBasis] = useState('current');
  const [asOf, setAsOf] = useState(defaultEndDate);
  const [approvedPeriod, setApprovedPeriod] = useState(defaultEndDate.slice(0, 7));
  const [dateType, setDateType] = useUrlParam(
    'dateType',
    DATE_TYPES.RANGE,
    (value) => Object.values(DATE_TYPES).includes(value),
  );
  const { startDate, endDate, setDateRange } = useDateRangeParams(defaultStartDate, defaultEndDate);

  // Month mode state
  const [monthParam, setMonthParam] = useUrlParam('month', serializeMonth(defaultMonth), isValidMonthParam);
  const monthYear = useMemo(
    () => parseMonthParam(monthParam, defaultMonth),
    [monthParam, defaultMonth.year, defaultMonth.monthIndex],
  );
  const monthInputRef = useRef(null);

  // Quarter mode state
  const [quarterParam, setQuarterParam] = useUrlParam('quarter', serializeQuarter(defaultQuarter), isValidQuarterParam);
  const quarter = useMemo(
    () => parseQuarterParam(quarterParam, defaultQuarter),
    [quarterParam, defaultQuarter.quarterYear, defaultQuarter.quarterIndex],
  );
  const { quarterYear, quarterIndex } = quarter;
  const [isQuarterPickerOpen, setIsQuarterPickerOpen] = useState(false);

  // Columns
  const columns = useMemo(() => ([
    { key: 'po_number', label: 'PO Number', type: 'string', sortable: true, sticky: true },
    { key: 'type', label: 'Type', type: 'string', sortable: true },
    { key: 'date_received', label: 'Date Received', type: 'date', sortable: true },
    { key: 'units_received', label: 'Units Received', type: 'number', sortable: true, align: 'right' },
    { key: 'units_ordered', label: 'Units Ordered', type: 'number', sortable: true, align: 'right' },
    { key: 'landed_cost', label: 'Landed Cost', type: 'currency', sortable: true, align: 'right' },
    { key: 'original_value', label: 'Original Value', type: 'currency', sortable: true, align: 'right' },
    { key: 'adjustments_value', label: 'Adjustments', type: 'currency', sortable: true, align: 'right' },
    { key: 'adjusted_value', label: 'Adjusted Value', type: 'currency', sortable: true, align: 'right' },
    { key: 'valuation_status', label: 'Valuation Status', type: 'string', sortable: true },
    { key: 'capture', label: 'Capture', type: 'string', sortable: true },
    { key: 'reconstructed_note', label: 'Reconstructed Note', type: 'string', sortable: true },
    { key: 'received_value', label: 'Received Value', type: 'currency', sortable: false, align: 'right' }
  ]), []);

  // Debounce PO search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedPoSearch(poSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [poSearch]);

  const effectiveRange = useMemo(() => {
    if (dateType === DATE_TYPES.RANGE) {
      return { startDate, endDate };
    }
    if (dateType === DATE_TYPES.MONTH) {
      return getMonthRange(monthYear.year, monthYear.monthIndex);
    }
    return getQuarterStartEnd(quarterYear, quarterIndex);
  }, [dateType, startDate, endDate, monthYear, quarterYear, quarterIndex]);

  const fetchData = useCallback(async () => {
    if (dateType === DATE_TYPES.RANGE && (!effectiveRange.startDate || !effectiveRange.endDate)) return;
    const { signal, requestId } = beginRequest();
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        startDate: effectiveRange.startDate,
        endDate: effectiveRange.endDate,
        page: String(currentPage),
        pageSize: String(pageSize),
        basis,
      });
      if (basis === 'as_of') params.append('as_of', asOf);
      if (basis === 'approved') params.append('period', approvedPeriod);
      if (sortField) {
        params.append('sortField', sortField);
        params.append('sortDirection', sortDirection);
      }
      if (debouncedPoSearch) {
        params.append('poNumber', debouncedPoSearch);
      }
      const res = await fetch(`/api/v1/reports/${MONTHLY_INBOUND_SHIPMENTS_VALUES}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        signal
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to fetch report data');
      }
      const json = await res.json();
      if (!isLatest(requestId)) return;
      // Trust API-provided received_value (computed per receipt row server-side)
      setData(json.data || []);
      setTotalByPage(Number(json.totals || 0));
      setTotalPages(json.totalPages || 1);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.error(e);
      if (!isLatest(requestId)) return;
      setError(e.message);
      setData([]);
      setTotalPages(1);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  }, [dateType, effectiveRange, currentPage, sortField, sortDirection, debouncedPoSearch, basis, asOf, approvedPeriod, beginRequest, isLatest]);

  useEffect(() => {
    if (!initialReady) return;
    fetchData();
  }, [initialReady, fetchData]);

  // Load Received Inventory Email settings on mount
  useEffect(() => {
    dispatch(getReportEmailSettings('received-inventory'));
    dispatch(getReportSendHistory('received-inventory'));
  }, [dispatch]);

  const handleDateChange = ({ startDate: s, endDate: e }) => {
    setDateRange({ startDate: s, endDate: e });
    setCurrentPage(1);
  };

  const handleSort = ({ field, direction }) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handleBackToReports = () => navigate(ROUTES.REPORTS);

  // Month controls
  const goPrevMonth = () => {
    const m = monthYear.monthIndex - 1;
    if (m < 0) {
      setMonthParam(serializeMonth({ year: monthYear.year - 1, monthIndex: 11 }));
    } else {
      setMonthParam(serializeMonth({ ...monthYear, monthIndex: m }));
    }
    setCurrentPage(1);
  };
  const goNextMonth = () => {
    const m = monthYear.monthIndex + 1;
    if (m > 11) {
      setMonthParam(serializeMonth({ year: monthYear.year + 1, monthIndex: 0 }));
    } else {
      setMonthParam(serializeMonth({ ...monthYear, monthIndex: m }));
    }
    setCurrentPage(1);
  };
  const onPickMonth = (e) => {
    // Use native month input (YYYY-MM)
    const value = e.target.value; // e.g., "2025-12"
    const [y, mm] = value.split('-').map(Number);
    if (y && mm) {
      setMonthParam(serializeMonth({ year: y, monthIndex: mm - 1 }));
      setCurrentPage(1);
    }
  };

  // Quarter controls
  const goPrevQuarter = () => {
    const q = quarterIndex - 1;
    if (q < 0) {
      setQuarterParam(serializeQuarter({ quarterYear: quarterYear - 1, quarterIndex: 3 }));
    } else {
      setQuarterParam(serializeQuarter({ quarterYear, quarterIndex: q }));
    }
    setCurrentPage(1);
  };
  const goNextQuarter = () => {
    const q = quarterIndex + 1;
    if (q > 3) {
      setQuarterParam(serializeQuarter({ quarterYear: quarterYear + 1, quarterIndex: 0 }));
    } else {
      setQuarterParam(serializeQuarter({ quarterYear, quarterIndex: q }));
    }
    setCurrentPage(1);
  };

  const currentQuarterLabel = `${quarters[quarterIndex].label} ${quarterYear}`;

  const filtersComponent = (
    <div className="contents">
      {/* Search by PO number */}
      <div className="flex flex-row items-center gap-2">
        <input
          type="text"
          value={poSearch}
          onChange={(e) => { setPoSearch(e.target.value); setCurrentPage(1); }}
          placeholder="Search PO #"
          className="w-40 sm:w-[160px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>

      <div className="flex flex-row items-center gap-2">
        <SelectDropdown
          selectClassname="!w-40 !bg-white"
          options={[
            { label: 'Current value', value: 'current' },
            { label: 'Original value', value: 'original' },
            { label: 'As of date', value: 'as_of' },
            { label: 'Approved period', value: 'approved' },
          ]}
          value={basis}
          onChange={(value) => { setBasis(value); setCurrentPage(1); }}
        />
        {basis === 'as_of' && (
          <input
            type="date"
            value={asOf}
            onChange={(event) => { setAsOf(event.target.value); setCurrentPage(1); }}
            className="rounded-md border border-gray-300 px-2 py-2 text-sm"
            aria-label="Valuation as of date"
          />
        )}
        {basis === 'approved' && (
          <input
            type="month"
            value={approvedPeriod}
            onChange={(event) => { setApprovedPeriod(event.target.value); setCurrentPage(1); }}
            className="rounded-md border border-gray-300 px-2 py-2 text-sm"
            aria-label="Approved period"
          />
        )}
      </div>

      {/* Date type selector */}
      <div className="flex flex-row items-center gap-2">
        <SelectDropdown
            selectClassname="!w-36 !bg-white"
            options={[
                { label: 'Date Range', value: DATE_TYPES.RANGE },
                { label: 'Month', value: DATE_TYPES.MONTH },
                { label: 'Quarter', value: DATE_TYPES.QUARTER }
            ]}
            value={dateType}
            onChange={(value) => { setDateType(value); setCurrentPage(1); }}
        />
      </div>

      {/* Range */}
      {dateType === DATE_TYPES.RANGE && (
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onDateChange={handleDateChange}
          label="Date Range"
          pickerDateFormat="MM/dd/yyyy"
          summaryDateFormat="MM/dd/yyyy"
          error={error && error.includes('date') ? error : null}
        />
      )}

      {/* Month */}
      {dateType === DATE_TYPES.MONTH && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={goPrevMonth}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 p-2 hover:bg-gray-50"
            title="Previous month"
          >
            <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
          </button>
          <button
            type="button"
            className="px-3 py-2 rounded-md border border-gray-300 bg-white text-sm w-[140px]"
            onClick={() => {
              const el = monthInputRef.current;
              if (el && typeof el.showPicker === 'function') {
                try { el.showPicker(); } catch (e) { el.click(); }
              } else if (el) {
                el.click();
              }
            }}
            title="Pick month and year"
          >
            {monthLabel(monthYear.year, monthYear.monthIndex)}
          </button>
          <input
            type="month"
            ref={monthInputRef}
            className="absolute opacity-0 w-0 h-0 pointer-events-none"
            onChange={onPickMonth}
            value={`${monthYear.year}-${String(monthYear.monthIndex + 1).padStart(2, '0')}`}
          />
          <button
            type="button"
            onClick={goNextMonth}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 p-2 hover:bg-gray-50"
            title="Next month"
          >
            <ChevronRightIcon className="h-4 w-4 text-gray-600" />
          </button>
        </div>
      )}

      {/* Quarter */}
      {dateType === DATE_TYPES.QUARTER && (
        <div className="relative flex items-center gap-2">
          <button
            type="button"
            onClick={goPrevQuarter}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 p-2 hover:bg-gray-50"
            title="Previous quarter"
          >
            <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
          </button>
          <button
            type="button"
            className="px-3 py-2 rounded-md border border-gray-300 bg-white text-sm w-[110px]"
            onClick={() => setIsQuarterPickerOpen((v) => !v)}
            title="Pick quarter and year"
          >
            {currentQuarterLabel}
          </button>
          <button
            type="button"
            onClick={goNextQuarter}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 p-2 hover:bg-gray-50"
            title="Next quarter"
          >
            <ChevronRightIcon className="h-4 w-4 text-gray-600" />
          </button>

          {isQuarterPickerOpen && (
            <div className="absolute top-full mt-2 z-10 bg-white border border-gray-200 rounded-md shadow w-64 p-3">
              <div className="flex items-center justify-between mb-3">
                <button
                  className="px-2 py-1 border rounded text-sm"
                  onClick={() => setQuarterParam(serializeQuarter({ quarterYear: quarterYear - 1, quarterIndex }))}
                >
                  {quarterYear - 1}
                </button>
                <div className="font-medium">{quarterYear}</div>
                <button
                  className="px-2 py-1 border rounded text-sm"
                  onClick={() => setQuarterParam(serializeQuarter({ quarterYear: quarterYear + 1, quarterIndex }))}
                >
                  {quarterYear + 1}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {quarters.map((q, idx) => (
                  <button
                    key={q.label}
                    className={`px-2 py-2 border rounded text-sm ${idx === quarterIndex ? 'bg-blue-50 border-blue-300' : 'bg-white'}`}
                    onClick={() => { setQuarterParam(serializeQuarter({ quarterYear, quarterIndex: idx })); setIsQuarterPickerOpen(false); setCurrentPage(1); }}
                  >
                    {q.label} {quarterYear}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <Cog8ToothIcon
            className="h-5 w-5 text-primary cursor-pointer"
            onClick={() => {
                setIsOpenSettings(true);
            }}
        />
      </div>
    </div>
  );

  const handleSuccessSubmit = async (data) => {
    const payload = {
      emailRecipients: data?.emailRecipients || [],
      scheduleType: data?.schedule || null,
      dayOfMonth: data?.dayOfMonth ?? null,
      slackChannelId: data?.slackChannelId ?? null,
      dayOfWeek: data?.dayOfWeek ?? null,
      timeOfDay: data?.timeOfDay ?? null,
      emailMessage: data?.emailMessage ?? null,
      emailSendPreviewTo: data?.emailSendPreviewTo ?? null
    };
    
    try {
      await dispatch(updateReportEmailSettings({ reportType: 'received-inventory', ...payload })).unwrap();
      const cronInterval = buildCronInterval({
        scheduleType: payload.scheduleType,
        dayOfMonth: payload.dayOfMonth,
        dayOfWeek: payload.dayOfWeek,
        timeOfDay: payload.timeOfDay
      });
      if (!cronInterval) {
        toast.error('Invalid schedule settings (cron expression could not be built)', { position: 'bottom-right' });
        return;
      }
      const cronData = {
        id: emailSettings?.cronId,
        type: emailSettings?.cronType,
        cronInterval
      };
      await dispatch(updateCronInformation({ singleData: cronData })).unwrap();
      toast.success('Settings saved successfully', { position: 'bottom-right' });
      setIsOpenSettings(false);
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to save settings', { position: 'bottom-right' });
    }
  };

  const sendPreviewEmail = async (data) => {
    try {
      const payload = {
        email: data?.emailSendPreviewTo,
        emailMessage: data?.emailMessage || '',
        type: data?.type || 'monthly',
        startDate: startDate,
        endDate: endDate,
        sortField: sortField || null,
        sortDirection: sortDirection || null,
        poNumber: debouncedPoSearch || null
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'received-inventory', ...payload })).unwrap();
      await dispatch(getReportSendHistory('received-inventory')).unwrap();
      toast.success('Preview email sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send preview', { position: 'bottom-right' });
    }
  };

  const sendPreviewSlack = async (data) => {
    try {
      const payload = {
        slackChannelId: data?.slackChannelId,
        emailMessage: data?.emailMessage || '',
        type: data?.type || 'monthly',
        startDate: startDate,
        endDate: endDate,
        sortField: sortField || null,
        sortDirection: sortDirection || null,
        poNumber: debouncedPoSearch || null
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'received-inventory', ...payload })).unwrap();
      await dispatch(getReportSendHistory('received-inventory')).unwrap();
      toast.success('Slack preview sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send slack preview', { position: 'bottom-right' });
    }
  };

  const exportButton = (
    <CSVExportButton
      data={data}
      columns={[
        { key: 'po_number', label: 'PO Number' },
        { key: 'type', label: 'Type' },
        { key: 'date_received', label: 'Date Received' },
        { key: 'units_received', label: 'Units Received' },
      { key: 'units_ordered', label: 'Units Ordered' },
      { key: 'landed_cost', label: 'Landed Cost' },
      { key: 'original_value', label: 'Original Value' },
      { key: 'adjustments_value', label: 'Adjustments' },
      { key: 'adjusted_value', label: 'Adjusted Value' },
      { key: 'valuation_status', label: 'Valuation Status' },
      { key: 'capture', label: 'Capture' },
      { key: 'reconstructed_note', label: 'Reconstructed Note' },
      { key: 'received_value', label: 'Received Value' }
      ]}
      filename={`${MONTHLY_INBOUND_SHIPMENTS_VALUES}`}
      onExport={async () => {
        const params = new URLSearchParams({
          startDate: effectiveRange.startDate,
          endDate: effectiveRange.endDate
        });
        if (sortField) {
          params.append('sortField', sortField);
          params.append('sortDirection', sortDirection);
        }
        if (debouncedPoSearch) {
          params.append('poNumber', debouncedPoSearch);
        }
        params.append('basis', basis);
        if (basis === 'as_of') params.append('as_of', asOf);
        if (basis === 'approved') params.append('period', approvedPeriod);
        const res = await fetch(`/api/v1/reports/${MONTHLY_INBOUND_SHIPMENTS_VALUES}/export?${params.toString()}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Export failed');
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const contentDisposition = res.headers.get('content-disposition');
        const filename = contentDisposition
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
          : `${MONTHLY_INBOUND_SHIPMENTS_VALUES}_${new Date().toISOString().slice(0, 10)}.csv`;
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return { success: true, handled: true };
      }}
    />
  );

  const paginationComponent = totalPages > 1 ? (
    <div className="flex items-center justify-between">
      <div className="text-sm text-gray-700">
        Page {currentPage} of {totalPages}
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Previous
        </button>
        <button
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Next
        </button>
      </div>
    </div>
  ) : null;

  const formatCurrency = (value) => {
    // console.log('formatCurrency', value);
    const num = Number(value);
    if (Number.isNaN(num)) return '';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  };

  const renderRows = () => {
    const formatRangeCurrency = (value) => {
      if (value === null || value === undefined || value === '') return '';
      if (typeof value === 'number') return formatCurrency(value);
      const s = String(value);
      if (!s.includes('-')) return isNaN(Number(s)) ? s : formatCurrency(Number(s));
      const [a, b] = s.split('-').map((x) => Number(String(x).trim()));
      if (isNaN(a) || isNaN(b)) return s; // fallback
      const aFixed = Number(a.toFixed(2));
      const bFixed = Number(b.toFixed(2));
      if (aFixed === 0 && bFixed > 0) return formatCurrency(bFixed);
      if (bFixed === 0 && aFixed > 0) return formatCurrency(aFixed);
      if (aFixed === bFixed) return formatCurrency(aFixed);
      return `${formatCurrency(aFixed)} - ${formatCurrency(bFixed)}`;
    };
    const formatRangeDate = (value) => {
      if (!value) return '';
      const s = String(value).trim();
      const toMDY = (ymd) => {
        const t = String(ymd).trim();
        // Already MDY
        if (t.includes('/')) return t;
        const parts = t.split('-').map((x) => String(x).trim());
        if (parts.length === 3) {
          const [yyyy, mm, dd] = parts;
          const MM = String(mm).padStart(2, '0');
          const DD = String(dd).padStart(2, '0');
          return `${MM}/${DD}/${yyyy}`;
        }
        return t;
      };
      // Split by range separator " - " specifically to avoid breaking YYYY-MM-DD
      const rangeParts = s.split(' - ');
      if (rangeParts.length === 1) {
        return toMDY(rangeParts[0]);
      }
      const left = toMDY(rangeParts[0]);
      const right = toMDY(rangeParts[1]);
      if (left === right) return left;
      return `${left} - ${right}`;
    };
    return data.map((row, idx) => (
      <tr key={`${row.po_number || 'po'}-${idx}`} className="bg-white">
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {row?.po_id ? (
            <a
              href={`${ROUTES.SHIPPING}?search=${encodeURIComponent(String(row.po_number || ''))}#${row.po_id}`}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline"
              title="Open in Shipping"
            >
              {row.po_number}
            </a>
          ) : (
            row.po_number
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.type}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatRangeDate(row.date_received)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{Number(row.units_received || 0).toLocaleString()}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{Number(row.units_ordered || 0).toLocaleString()}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatRangeCurrency(row.landed_cost)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(row.original_value)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(row.adjustments_value)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(row.adjusted_value)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.valuation_status}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.capture}</td>
        <td className="px-6 py-4 text-sm text-gray-900">{row.reconstructed_note}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(row.received_value)}</td>
      </tr>
    ));
  };

  const breadcrumbs = [
    { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
    { label: '' }
  ];

  return (
    <>
        <ReportTemplate
            reportId={MONTHLY_INBOUND_SHIPMENTS_VALUES}
            description="Received inventory valuation by Purchase Order, using preserved receipt values and adjustments"
            breadcrumbs={breadcrumbs}
            onBack={handleBackToReports}
            marginFromSidebar={marginFromSidebar}
            totalByPage={totalByPage}
            isLoading={isLoading}
            error={error}
            filters={filtersComponent}
            exportButton={exportButton}
            data={data}
            columns={columns}
            pagination={paginationComponent}
            emptyMessage="No inbound shipment value data for the selected period."
            onSort={handleSort}
            cronType={emailSettings?.cronType || null}
            sortField={sortField}
            sortDirection={sortDirection}
            enableSorting={true}
        >
            {renderRows()}
        </ReportTemplate>
        <ReportEmailSettingsSidebar
        reportType={'received-inventory'}
            isOpen={isOpenSettings}
            marginFromSidebar={marginFromSidebar}
            onClose={() => setIsOpenSettings(false)}
            isLoading={isLoadingEmailSettings}
            onSuccessSubmit={handleSuccessSubmit}
            sendPreviewEmail={sendPreviewEmail}
            sendPreviewSlack={sendPreviewSlack}
            defaultSettings={emailSettings || {}}
            sendHistory={sendHistory}
            isLoadingSendHistory={isLoadingSendHistory}
            downloadFilenamePrefix="received-inventory"
        />
    </>
  );
};

export default MonthlyInboundShipmentsValues;
